import { z } from 'zod';
/**
 * **Quel immeuble** porte l'anomalie — `Anomaly.building`
 * ([M34](../../../../docs/MODELE-DONNEES.md#m34), tranché le 21/09/2026).
 *
 * Les trois valeurs et leurs libellés viennent du prototype validé, section « Éléments techniques
 * relevés · **par bâtiment** » du constructeur d'anomalie (`mocks/ghp-ct/screens.js`) :
 *
 * ```html
 * <div class="seg" data-anom-bat>
 *   <button class="seg-btn on" data-abat="maison">Maison</button>
 *   <button class="seg-btn"    data-abat="ar">AR</button>
 *   <button class="seg-btn"    data-abat="av">AV</button>
 * </div>
 * ```
 *
 * `AR` et `AV` sont les bâtiments **arrière** et **avant** d'une même parcelle : le vocabulaire
 * de la fiche papier de GHP, où une anomalie relevée dans un logement de fond de cour ne se
 * confond pas avec celle du bâtiment sur rue. Sans cette colonne, deux anomalies identiques
 * relevées dans les deux bâtiments d'une même parcelle sont indiscernables sur le rapport — et
 * c'est le rapport qui dit au propriétaire où faire les travaux.
 *
 * ⚠️ **Une énumération figée, pas une liste de `vocabulary`** — la ligne de partage de
 * [M17](../../../../docs/MODELE-DONNEES.md#m17) : les listes que GHP administre sont celles dont
 * le contenu bouge (les origines, les destinations), pas celles qui décrivent la **géométrie
 * d'une parcelle**. Il n'y a jamais eu que ces trois cases sur la fiche, et un quatrième
 * bâtiment n'est pas une valeur à ajouter : ce serait un autre modèle d'adresse.
 *
 * ⚠️ **Codes en anglais, libellés en français.** Les trois valeurs sont des identifiants sur
 * lesquels le code branche, donc soumises à la règle générale — l'exception aux codes en clair
 * ne vaut que pour les `code` de `vocabulary`, qui sont des **données** (§ *Convention de
 * nommage*). « Maison », « AR » et « AV » restent ce qui s'affiche et s'imprime.
 */
export const ANOMALY_BUILDINGS = ['house', 'rear', 'front'];
export const anomalyBuildingSchema = z.enum(ANOMALY_BUILDINGS);
/**
 * Les libellés de la fiche papier, **verbatim** — « AR » et « AV » ne se développent pas en
 * « Arrière » et « Avant » : ce sont les deux mentions que les agents lisent et écrivent depuis
 * toujours, et elles tiennent dans la colonne étroite du constructeur d'anomalies.
 *
 * Ici plutôt qu'à l'écran, pour la raison de `INQUIRY_PURPOSE_LABELS` : le terrain les coche, le
 * back-office les relit sur la fiche de validation et le rapport les imprime. Trois copies
 * finiraient par diverger, et c'est le rapport client qui porterait l'écart.
 */
export const ANOMALY_BUILDING_LABELS = {
    house: 'Maison',
    rear: 'AR',
    front: 'AV',
};
/**
 * Le segment **présélectionné** par le prototype — « Maison » porte la classe `on`.
 *
 * ⚠️ **Il vit ici et pas dans l'écran, et ce n'est pas la même chose que `null`.** L'immense
 * majorité des parcelles n'a qu'un bâtiment : l'agent ne touche pas au sélecteur, et l'anomalie
 * part donc avec cette valeur-là. Le dire une fois évite que la tablette présélectionne « Maison »
 * pendant que le rapport, devant la même anomalie, imprime autre chose.
 *
 * ⚠️ **La colonne reste pourtant nullable, et ce n'est pas une contradiction** : `null` veut dire
 * « personne n'a jamais posé la question » — une anomalie relevée avant que cette colonne
 * n'existe. Remplir ces lignes-là avec `house` fabriquerait un **constat** que nul n'a fait, ce
 * qu'une anomalie gelée interdit ([M19](../../../../docs/MODELE-DONNEES.md#m19)). Le défaut est
 * celui de la **saisie**, pas celui de la colonne — la migration `0030` ne rétro-remplit rien.
 */
export const DEFAULT_ANOMALY_BUILDING = 'house';
//# sourceMappingURL=anomaly-building.js.map